import { useEffect, useMemo, useRef, useState } from "react";
import { Send, CheckCircle, MessageCircle, ImagePlus, X, Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { getAttribution, trackLeadSubmit } from "@/lib/tracking";
import {
  ASSURANCE_OPTIONS,
  LEAD_PHOTOS_BUCKET,
  MAX_LEAD_PHOTOS,
  TYPE_DEMANDE_OPTIONS,
  photoExtension,
  validateLeadPhoto,
  whatsappLink,
} from "@/lib/leads";

const optional = (max: number, msg: string) =>
  z.string().trim().max(max, { message: msg }).optional().transform((v) => (v ? v : null));

const leadSchema = z.object({
  nom: z.string().trim().min(1, { message: "Indiquez votre nom." }).max(100, { message: "Nom trop long." }),
  telephone: z
    .string()
    .trim()
    .max(30, { message: "Numéro trop long." })
    .refine((v) => /^\+?[\d\s().-]+$/.test(v) && v.replace(/\D/g, "").length >= 9, {
      message: "Indiquez un numéro de téléphone valide.",
    }),
  email: z
    .string()
    .trim()
    .max(255, { message: "Email trop long." })
    .optional()
    .refine((v) => !v || z.string().email().safeParse(v).success, { message: "Email invalide." })
    .transform((v) => (v ? v : null)),
  vehicule: optional(120, "Véhicule : 120 caractères max."),
  type_demande: z.enum(TYPE_DEMANDE_OPTIONS, { errorMap: () => ({ message: "Choisissez le type de demande." }) }),
  assurance: z
    .union([z.enum(ASSURANCE_OPTIONS), z.literal("")])
    .optional()
    .transform((v) => (v ? v : null)),
  message: optional(2000, "Message : 2000 caractères max."),
});

const fieldClass =
  "w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow";

const WHATSAPP_PHOTOS = whatsappLink("Bonjour, je viens d'envoyer une demande de devis sur proimpact.ch. Voici les photos des dégâts :");

const ContactForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const previews = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const addPhotos = (list: FileList | null) => {
    if (!list) return;
    const next = [...photos];
    let err: string | null = null;
    for (const file of Array.from(list)) {
      const problem = validateLeadPhoto(file);
      if (problem) { err = problem; continue; }
      if (next.length >= MAX_LEAD_PHOTOS) { err = `${MAX_LEAD_PHOTOS} photos maximum.`; break; }
      next.push(file);
    }
    setPhotos(next);
    setErrors((e) => ({ ...e, photos: err ?? "" }));
    if (fileInput.current) fileInput.current.value = "";
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sending) return;
    const data = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    if (data.website) return; // honeypot: bots fill hidden fields

    const parsed = leadSchema.safeParse(data);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) errs[String(issue.path[0])] ??= issue.message;
      setErrors(errs);
      return;
    }
    setErrors({});
    setFormError(null);
    setSending(true);

    try {
      const id = crypto.randomUUID();
      const paths: string[] = [];
      for (const [i, file] of photos.entries()) {
        const path = `${id}/${i + 1}-${Math.random().toString(36).slice(2, 8)}.${photoExtension(file)}`;
        const { error } = await supabase.storage
          .from(LEAD_PHOTOS_BUCKET)
          .upload(path, file, { contentType: file.type || undefined, upsert: false });
        if (error) throw new Error("photo");
        paths.push(path);
      }

      const attribution = getAttribution();
      const v = parsed.data as z.output<typeof leadSchema> & { nom: string; telephone: string; type_demande: string };
      const { error } = await supabase.from("leads").insert({
        id,
        nom: v.nom,
        telephone: v.telephone,
        email: v.email,
        vehicule: v.vehicule,
        type_demande: v.type_demande,
        assurance: v.assurance,
        message: v.message,
        photos: paths,
        page_source: window.location.pathname.slice(0, 500),
        utm_source: attribution.utm_source ?? null,
        utm_campaign: attribution.utm_campaign ?? null,
        gclid: attribution.gclid ?? null,
      });
      if (error) throw error;

      // Stored: count the conversion, then notify the team (non-blocking).
      trackLeadSubmit();
      supabase.functions.invoke("notify-lead", { body: { lead_id: id } }).catch(() => {});
      setSubmitted(true);
    } catch (err) {
      setFormError(
        err instanceof Error && err.message === "photo"
          ? "L'envoi d'une photo a échoué. Réessayez, ou envoyez-les-nous sur WhatsApp."
          : "L'envoi a échoué. Réessayez ou appelez-nous au +41 76 638 86 55.",
      );
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="glass-card rounded-lg p-8 text-center max-w-lg mx-auto" role="status">
        <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
        <h3 className="text-xl font-bold mb-2">Merci !</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Nous vous rappelons sous 2 h (jours ouvrés). Pour aller plus vite, envoyez vos photos sur WhatsApp.
        </p>
        <Button asChild className="w-full gap-2 h-12 text-base">
          <a href={WHATSAPP_PHOTOS} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-5 h-5" /> Envoyer mes photos sur WhatsApp
          </a>
        </Button>
      </div>
    );
  }

  const err = (k: string) =>
    errors[k] ? <p className="mt-1 text-xs text-destructive" id={`err-${k}`}>{errors[k]}</p> : null;

  return (
    <form
      onSubmit={handleSubmit}
      onChange={(e) => {
        const name = (e.target as HTMLInputElement).name;
        if (name && errors[name]) setErrors(({ [name]: _removed, ...rest }) => rest);
      }}
      noValidate
      className="glass-card rounded-lg p-6 sm:p-8 max-w-lg mx-auto">
      <h3 className="text-xl font-bold tracking-tight mb-1">Demander un devis gratuit</h3>
      <p className="text-sm text-muted-foreground mb-6">
        Décrivez votre besoin, nous vous répondons sous 24h.
      </p>

      {/* honeypot */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="lead-nom" className="block text-sm font-medium mb-1.5">
              Nom <span className="text-destructive">*</span>
            </label>
            <input id="lead-nom" name="nom" type="text" required autoComplete="name" maxLength={100}
              aria-invalid={!!errors.nom} className={fieldClass} placeholder="Votre nom" />
            {err("nom")}
          </div>
          <div>
            <label htmlFor="lead-telephone" className="block text-sm font-medium mb-1.5">
              Téléphone <span className="text-destructive">*</span>
            </label>
            <input id="lead-telephone" name="telephone" type="tel" required autoComplete="tel" inputMode="tel" maxLength={30}
              aria-invalid={!!errors.telephone} className={fieldClass} placeholder="+41 7X XXX XX XX" />
            {err("telephone")}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="lead-email" className="block text-sm font-medium mb-1.5">Email</label>
            <input id="lead-email" name="email" type="email" autoComplete="email" maxLength={255}
              aria-invalid={!!errors.email} className={fieldClass} placeholder="votre@email.ch (facultatif)" />
            {err("email")}
          </div>
          <div>
            <label htmlFor="lead-vehicule" className="block text-sm font-medium mb-1.5">Véhicule</label>
            <input id="lead-vehicule" name="vehicule" type="text" maxLength={120}
              className={fieldClass} placeholder="Marque / modèle" />
            {err("vehicule")}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="lead-type" className="block text-sm font-medium mb-1.5">
              Type de demande <span className="text-destructive">*</span>
            </label>
            <select id="lead-type" name="type_demande" required defaultValue=""
              aria-invalid={!!errors.type_demande} className={fieldClass}>
              <option value="" disabled>Choisir…</option>
              {TYPE_DEMANDE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {err("type_demande")}
          </div>
          <div>
            <label htmlFor="lead-assurance" className="block text-sm font-medium mb-1.5">Assurance</label>
            <select id="lead-assurance" name="assurance" defaultValue="" className={fieldClass}>
              <option value="">Choisir…</option>
              {ASSURANCE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="lead-message" className="block text-sm font-medium mb-1.5">Message</label>
          <textarea id="lead-message" name="message" rows={3} maxLength={2000}
            className={`${fieldClass} resize-none`}
            placeholder="Nature des dégâts, numéro de sinistre si applicable..." />
          {err("message")}
        </div>

        <div>
          <span className="block text-sm font-medium mb-1.5">Photos des dégâts (jusqu'à {MAX_LEAD_PHOTOS})</span>
          <input ref={fileInput} id="lead-photos" type="file" accept="image/*" multiple className="sr-only"
            onChange={(e) => addPhotos(e.target.files)} />
          {photos.length > 0 && (
            <ul className="mb-2 grid grid-cols-5 gap-2">
              {photos.map((f, i) => (
                <li key={`${f.name}-${i}`} className="relative aspect-square overflow-hidden rounded-md border border-input">
                  <img src={previews[i]} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                    className="absolute right-0.5 top-0.5 rounded-full bg-background/90 p-0.5"
                    aria-label={`Retirer la photo ${i + 1}`}>
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {photos.length < MAX_LEAD_PHOTOS && (
            <label htmlFor="lead-photos"
              className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-input px-4 py-3 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <ImagePlus className="h-4 w-4" /> Ajouter des photos (10 Mo max chacune)
            </label>
          )}
          {err("photos")}
        </div>

        {formError && <p className="text-sm text-destructive" role="alert">{formError}</p>}

        <Button type="submit" disabled={sending} className="w-full gap-2 h-12 text-base">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "Envoi en cours…" : "Envoyer la demande"}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          En soumettant ce formulaire, vous acceptez notre{" "}
          <a href="/politique-confidentialite" className="underline hover:text-primary">
            politique de confidentialité
          </a>.
        </p>
      </div>
    </form>
  );
};

export default ContactForm;
